<script>
import Layout from "../../layouts/main";
import appConfig from "@/app.config";
import firebase from "firebase/app";
import "firebase/firestore";
import moment from "moment";
import Swal from "sweetalert2";
import axios from "axios";
import { cargarReferidos, resumenReferidos, COMISION_POR_REFERIDO } from "@/services/referidos";

const BASE_URL = "https://saiteqa7r4.execute-api.us-west-2.amazonaws.com/UAT";
const CACHE_TTL_MS = 10 * 60 * 1000; // 10 min
const STRIPE_CACHE_KEY = "recnet_plus_stripe_cache_v6";
const LIST_CACHE_KEY = "recnet_plus_active_list_cache_v6";

export default {
    page: {
        title: "Recnet Plus",
        meta: [{ name: "description", content: appConfig.description }],
    },
    components: { Layout },
    data() {
        return {
            title: "Recnet Plus",
            items: [{ text: "Recnet Plus", active: true }],

            // Firestore docs
            docsUsuarios: [],
            docsAppUsuarios: [],
            docsAppComercios: [],
            docsComercios: [],
            docsEmpresas: [],

            // Enriquecidos
            allUsers: [],
            plusUsers: [],

            // Índice Stripe masivo
            subscriptionsIndex: {},
            subscriptionsLoaded: false,


            // UI
            loadingUsers: true,
            searchQuery: "",
            showUserModal: false,
            selectedUser: null,

            // Paginación
            currentPage: 1,
            perPage: 8,

            // Cache Stripe puntual
            stripeCache: {},

            // Concurrencia
            debounceTimer: null,
            runId: 0,
            firstProcessDone: false,
            initialFallbackTimer: null,
            unsubs: [],

            // Referidos en modal
            refPlus: [],
            refNo: [],
            refResumen: null,
            comision: COMISION_POR_REFERIDO,
            refLoading: false,
        };
    },
    computed: {
        filteredPlusUsers() {
            const arr = this.plusUsers;
            if (!this.searchQuery) return arr;
            const q = this.searchQuery.toLowerCase();
            return arr.filter((u) =>
                [u.name, u.phone, u.email, u.correo]
                    .filter(Boolean)
                    .some((v) => String(v).toLowerCase().includes(q))
            );
        },
        totalRows() {
            return this.filteredPlusUsers.length;
        },
    },
    watch: {
        searchQuery() { this.currentPage = 1; },
    },
    methods: {
        // ===== Utils =====
        sleep(ms) { return new Promise((res) => setTimeout(res, ms)); },
        formatoMoneda(valor) {
            return new Intl.NumberFormat("es-MX", {
                style: "currency", currency: "MXN", maximumFractionDigits: 0,
            }).format(valor || 0);
        },
        pick(obj, keys) {
            for (const k of keys) {
                if (obj && Object.prototype.hasOwnProperty.call(obj, k) && obj[k]) return obj[k];
            }
            return undefined;
        },
        looksLikeEmail(v) {
            if (typeof v !== "string") return false;
            return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim());
        },
        findEmailDeep(obj, depth = 0) {
            if (!obj || depth > 3) return null;
            if (typeof obj === "string" && this.looksLikeEmail(obj)) return obj.trim().toLowerCase();
            if (typeof obj !== "object") return null;
            const direct = this.pick(obj, [
                "email", "correo", "mail", "correoUsuario", "Email", "emailEmpresa", "company_email",
            ]);
            if (this.looksLikeEmail(direct)) return direct.trim().toLowerCase();
            for (const key of Object.keys(obj)) {
                const v = obj[key];
                const found = this.findEmailDeep(v, depth + 1);
                if (found) return found;
            }
            return null;
        },
        extractEmail(u) {
            const direct = this.pick(u, [
                "email", "correo", "mail", "correoUsuario", "Email", "emailEmpresa", "company_email",
            ]);
            if (this.looksLikeEmail(direct)) return direct.trim().toLowerCase();
            return this.findEmailDeep(u) || "";
        },
        extractStripeId(u) {
            const v = this.pick(u, [
                "stripe_id", "stripeId", "stripeCustomerId", "customer_id",
                "stripeCustomer", "customerId", "stripe_customer_id", "stripeCustomerID",
            ]);
            return (typeof v === "string" && v.trim()) ? v.trim() : null;
        },
        extractName(u) {
            const direct = this.pick(u, [
                "name", "nombre", "fullName", "displayName",
                "nombreEmpresa", "razonSocial", "companyName", "empresa", "comercio", "nombre_comercio",
            ]);
            if (direct) return String(direct);
            const fn = this.pick(u, ["firstName", "nombre1", "primerNombre"]);
            const ln = this.pick(u, ["lastName", "apellido", "apellido1", "primerApellido"]);
            if (fn || ln) return [fn, ln].filter(Boolean).join(" ");
            return "";
        },
        extractPhone(u) {
            const v = this.pick(u, [
                "phone", "telefono", "celular", "phoneNumber", "tel",
                "telefonoEmpresa", "phoneCompany",
            ]);
            return v ? String(v) : "";
        },
        extractOwnReferralCode(u) {
            const c = this.pick(u, [
                "referral_code", "referralCode", "codigoReferido", "codigo_referido",
                "referal_code", "refCode", "ref_code", "refcode",
            ]);
            return c ? String(c).trim() : "";
        },
        extractUsedReferralCode(u) {
            const used = this.pick(u, [
                "referred_by", "used_referral_code", "referred_by_code", "referrer_code",
                "invitadoPorCodigo", "invited_by_code", "referral_parent_code",
                "cameFromCode", "came_from_code", "referredBy", "referred_by_company",
            ]);
            return used ? String(used).trim() : "";
        },
        extractCreatedAtFormatted(u) {
            const v = this.pick(u, [
                "creation_timestamp", "createdAt", "creadoEn", "fechaCreacion",
                "created_on", "created_at", "creado_el",
            ]);
            if (typeof v === "string" && /^\d+$/.test(v)) return moment(Number(v)).format("DD/MM/YYYY");
            if (typeof v === "number") return moment(v < 2e10 ? v * 1000 : v).format("DD/MM/YYYY");
            if (v && typeof v.toDate === "function") return moment(v.toDate()).format("DD/MM/YYYY");
            return "—";
        },
        toSeconds(ts) {
            const n = Number(ts);
            if (!isFinite(n) || n <= 0) return null;
            return n >= 1e12 ? Math.floor(n / 1000) : Math.floor(n);
        },
        // acepta seg/ms/ISO y devuelve seg
        parseToSeconds(val) {
            if (!val) return null;
            if (typeof val === "number") return this.toSeconds(val);
            if (typeof val === "string") {
                if (/^\d+$/.test(val)) return this.toSeconds(Number(val));
                const m = moment(val, [moment.ISO_8601, "YYYY-MM-DD", "DD/MM/YYYY", "DD-MM-YYYY"], true);
                if (m.isValid()) return Math.floor(m.valueOf() / 1000);
            }
            return null;
        },

        // ===== Cache local =====
        loadStripeCache() {
            try {
                const raw = localStorage.getItem(STRIPE_CACHE_KEY);
                if (!raw) return;
                const parsed = JSON.parse(raw);
                if (parsed && typeof parsed === "object") this.stripeCache = parsed;
            } catch (err) { console.warn("[RecnetPlus] loadStripeCache error:", err); }
        },
        saveStripeCache() {
            try {
                localStorage.setItem(STRIPE_CACHE_KEY, JSON.stringify(this.stripeCache));
            } catch (err) { console.warn("[RecnetPlus] saveStripeCache error:", err); }
        },
        rehydrateList() {
            try {
                const raw = localStorage.getItem(LIST_CACHE_KEY);
                if (!raw) return;
                const parsed = JSON.parse(raw);
                if (Array.isArray(parsed)) {
                    this.plusUsers = parsed;
                    if (parsed.length) this.loadingUsers = false;
                }
            } catch (err) { console.warn("[RecnetPlus] rehydrateList error:", err); }
        },
        saveListCache() {
            try {
                localStorage.setItem(LIST_CACHE_KEY, JSON.stringify(this.plusUsers || []));
            } catch (err) { console.warn("[RecnetPlus] saveListCache error:", err); }
        },

        // ===== Concurrencia + reintentos =====
        async withRetries(fn, { tries = 3, baseDelay = 300 } = {}) {
            let lastErr;
            for (let i = 0; i < tries; i++) {
                try { return await fn(); }
                catch (err) {
                    lastErr = err;
                    const status = err?.response?.status;
                    if (!status || status === 429 || (status >= 500 && status <= 599)) {
                        await this.sleep(baseDelay * Math.pow(2, i));
                        continue;
                    }
                    break;
                }
            }
            throw lastErr;
        },
        async promisePool(items, worker, limit = 6) {
            const ret = [];
            let idx = 0;
            const runNext = async () => {
                const i = idx++;
                if (i >= items.length) return;
                ret[i] = await worker(items[i], i);
                return runNext();
            };
            const starters = Array(Math.min(limit, items.length)).fill(0).map(runNext);
            await Promise.all(starters);
            return ret;
        },

        // ===== Stripe helpers =====
        collectSubscriptionsFromResponse(data) {
            const buckets = [
                data?.active_subscription, data?.subscriptions, data?.data, data?.all,
                data?.all_subscriptions, data?.items, data?.results,
            ].filter(Boolean);
            const flat = [];
            for (const b of buckets) {
                if (Array.isArray(b)) for (const s of b) if (s && typeof s === "object") flat.push(s);
                else if (b && typeof b === "object" && Array.isArray(b.data))
                    for (const s of b.data) if (s && typeof s === "object") flat.push(s);
            }
            return flat;
        },
        pickBestSubscription(subs) {
            if (!subs.length) return null;
            const score = (s) => {
                const status = String(s.status || "").toLowerCase();
                const end = this.parseToSeconds(s.current_period_end) || 0;
                if (status === "active") return 1000000000 + end;
                if (status === "trialing") return 900000000 + end;
                if (status === "past_due") return 800000000 + end;
                if (status === "canceled") return 700000000 + end;
                return end;
            };
            let best = subs[0];
            for (const s of subs) if (score(s) > score(best)) best = s;
            return best;
        },
        async getCustomerStripeByEmail(email) {
            return this.withRetries(async () => {
                const { data } = await axios.get(`${BASE_URL}/customer/${encodeURIComponent(email)}`);
                return data?.customer?.data?.[0] || null;
            });
        },
        async getAnySubscriptionByCustomer(customerId) {
            return this.withRetries(async () => {
                const { data } = await axios.get(`${BASE_URL}/subscription/${customerId}`);
                const subs = this.collectSubscriptionsFromResponse(data);
                if (subs.length) return this.pickBestSubscription(subs);
                return data?.active_subscription?.[0] || null;
            });
        },

        // Índice masivo: { emailLower: { active, current_period_end(sec), status? } }
        async loadSubscriptionsIndex() {
            try {
                const { data } = await axios.get(`${BASE_URL}/users/subscriptions`, {
                    headers: { "Content-Type": "application/json" },
                });
                const arr = Array.isArray(data?.customers) ? data.customers : [];
                const idx = {};
                for (const it of arr) {
                    const email = (it.email || it.customer_email || "").toLowerCase().trim();
                    if (!email) continue;
                    const cpeSec = this.parseToSeconds(it.current_period_end ?? it.currentPeriodEnd);
                    idx[email] = {
                        active: !!it.active,
                        current_period_end: cpeSec || null,
                        status: it.status ? String(it.status).toLowerCase() : (it.active ? "active" : null),
                    };
                }
                this.subscriptionsIndex = idx;
                this.subscriptionsLoaded = true;
            } catch (err) {
                console.warn("[RecnetPlus] loadSubscriptionsIndex error:", err?.message || err);
                this.subscriptionsIndex = {};
                this.subscriptionsLoaded = true;
            }
        },

        // Si el índice no trae current_period_end, forzamos fetch puntual
        async getStripeInfoForUser(user, { ignoreCache = false } = {}) {
            const email = this.extractEmail(user);
            const candidateCustomerId = this.extractStripeId(user);
            const cacheKey = candidateCustomerId || email;
            const now = Date.now();

            // 1) Índice masivo (solo si ya trae CPE)
            if (email && this.subscriptionsIndex[email]) {
                const entry = this.subscriptionsIndex[email];
                const sec = this.parseToSeconds(entry.current_period_end);
                if (sec) {
                    const infoFromIndex = {
                        status: entry.status || (entry.active ? "active" : null),
                        current_period_end: sec,
                        cancel_at_period_end: false,
                        cachedAt: now,
                    };
                    if (cacheKey) {
                        this.stripeCache[cacheKey] = infoFromIndex;
                        this.saveStripeCache();
                    }
                    return infoFromIndex;
                }
                // Si no hay sec -> no retornamos; seguimos a fetch puntual
            }

            // 2) Cache puntual
            const cached = (!ignoreCache && cacheKey) ? this.stripeCache[cacheKey] : null;
            if (cached && now - (cached.cachedAt || 0) < CACHE_TTL_MS) return cached;

            // 3) Fallback puntual por cliente
            try {
                let customerId = candidateCustomerId;
                if (!customerId && email) {
                    const customer = await this.getCustomerStripeByEmail(email);
                    customerId = customer?.id || null;
                }
                let sub = null;
                if (customerId) sub = await this.getAnySubscriptionByCustomer(customerId);

                const sec = this.parseToSeconds(sub?.current_period_end);
                const info = {
                    status: (sub?.status || "").toLowerCase() || null,
                    current_period_end: sec || null,
                    cancel_at_period_end: !!(sub?.cancel_at_period_end),
                    cachedAt: now,
                };
                if (cacheKey) {
                    this.stripeCache[cacheKey] = info;
                    this.saveStripeCache();
                }
                return info;
            } catch (e) {
                console.warn("getStripeInfoForUser fallback", e);
                const info = { status: null, current_period_end: null, cancel_at_period_end: false, cachedAt: now };
                if (cacheKey) {
                    this.stripeCache[cacheKey] = info;
                    this.saveStripeCache();
                }
                return info;
            }
        },

        // ===== Firestore realtime =====
        subscribeUsers() {
            try {
                this.loadingUsers = true;
                const addUnsub = (fn) => { if (fn) this.unsubs.push(fn); };

                addUnsub(firebase.firestore().collection("usuarios").onSnapshot(
                    (snap) => {
                        this.docsUsuarios = snap.docs.map((d) => ({ id: d.id, ...d.data(), src: "usuarios" }));
                        this.scheduleProcess();
                    },
                    (err) => console.error("onSnapshot usuarios", err)
                ));

                addUnsub(firebase.firestore().collection("app_usuarios").onSnapshot(
                    (snap) => {
                        this.docsAppUsuarios = snap.docs.map((d) => ({ id: d.id, ...d.data(), src: "app_usuarios" }));
                        this.scheduleProcess();
                    },
                    (err) => console.error("onSnapshot app_usuarios", err)
                ));

                // Colecciones opcionales
                try {
                    addUnsub(firebase.firestore().collection("app_comercios").onSnapshot(
                        (snap) => {
                            this.docsAppComercios = snap.docs.map((d) => ({ id: d.id, ...d.data(), src: "app_comercios" }));
                            this.scheduleProcess();
                        },
                        (err) => console.warn("onSnapshot app_comercios", err)
                    ));
                } catch (err) { console.warn("[RecnetPlus] app_comercios no disponible:", err); }

                try {
                    addUnsub(firebase.firestore().collection("comercios").onSnapshot(
                        (snap) => {
                            this.docsComercios = snap.docs.map((d) => ({ id: d.id, ...d.data(), src: "comercios" }));
                            this.scheduleProcess();
                        },
                        (err) => console.warn("onSnapshot comercios", err)
                    ));
                } catch (err) { console.warn("[RecnetPlus] comercios no disponible:", err); }

                try {
                    addUnsub(firebase.firestore().collection("empresas").onSnapshot(
                        (snap) => {
                            this.docsEmpresas = snap.docs.map((d) => ({ id: d.id, ...d.data(), src: "empresas" }));
                            this.scheduleProcess();
                        },
                        (err) => console.warn("onSnapshot empresas", err)
                    ));
                } catch (err) { console.warn("[RecnetPlus] empresas no disponible:", err); }

                this.initialFallbackTimer = setTimeout(() => this.scheduleProcess(), 800);
            } catch (e) {
                console.error(e);
                Swal.fire("Error", "No se pudieron suscribir a los usuarios.", "error");
            }
        },

        scheduleProcess() {
            if (this.debounceTimer) clearTimeout(this.debounceTimer);
            this.debounceTimer = setTimeout(() => this.processUsersCoalesced(), 120);
        },

        // Une, enriquece y filtra SOLO activos
        async processUsersCoalesced() {
            const myRun = ++this.runId;

            // Merge por email
            const byEmail = new Map();
            const pushDoc = (u) => {
                const email = this.extractEmail(u);
                const key = email || `${u.src}:${u.id}`;
                if (!byEmail.has(key)) byEmail.set(key, u);
                else {
                    const existing = byEmail.get(key);
                    if (existing.src !== "usuarios" && u.src === "usuarios") {
                        byEmail.set(key, { ...existing, ...u });
                    } else {
                        byEmail.set(key, { ...u, ...existing });
                    }
                }
            };
            this.docsUsuarios.forEach(pushDoc);
            this.docsAppUsuarios.forEach(pushDoc);
            this.docsAppComercios.forEach(pushDoc);
            this.docsComercios.forEach(pushDoc);
            this.docsEmpresas.forEach(pushDoc);

            let merged = Array.from(byEmail.values());

            // Orden estable
            merged.sort((a, b) => {
                const an = (this.extractName(a) || "").toLowerCase();
                const bn = (this.extractName(b) || "").toLowerCase();
                if (an && bn && an !== bn) return an.localeCompare(bn);
                const ae = (this.extractEmail(a) || "").toLowerCase();
                const be = (this.extractEmail(b) || "").toLowerCase();
                return ae.localeCompare(be);
            });

            // Enriquecimiento
            const ACTIVE_STATUSES = new Set(["active", "trialing", "past_due"]);
            const now = moment();

            const worker = async (u) => {
                const email = this.extractEmail(u);
                const stripeId = this.extractStripeId(u);
                const name = this.extractName(u) || (email || "");
                const phone = this.extractPhone(u);

                let stripeActive = false;
                let stripeStatus = null;
                let currentPeriodEnd = null; // seg
                let vigencia = null;         // "DD/MM/YYYY"

                // 1) Índice masivo
                if (email && this.subscriptionsIndex[email]) {
                    const entry = this.subscriptionsIndex[email];
                    stripeActive = !!entry.active;
                    stripeStatus = entry.status || (entry.active ? "active" : null);
                    const sec = this.parseToSeconds(entry.current_period_end);
                    if (sec) {
                        currentPeriodEnd = sec;
                        vigencia = moment.unix(sec).format("DD/MM/YYYY");
                    }
                }

                // 2) Fallback puntual si falta CPE o no está activo
                if ((!currentPeriodEnd || !stripeActive) && (email || stripeId)) {
                    const subInfo = await this.getStripeInfoForUser(u);
                    stripeStatus = subInfo.status || stripeStatus;
                    const sec = this.parseToSeconds(subInfo.current_period_end);
                    if (sec && !currentPeriodEnd) {
                        currentPeriodEnd = sec;
                        vigencia = moment.unix(sec).format("DD/MM/YYYY");
                    }
                    const end = currentPeriodEnd ? moment.unix(currentPeriodEnd) : null;
                    const stillActiveUntilEnd = end && end.isSameOrAfter(now);
                    stripeActive =
                        (subInfo.status && ACTIVE_STATUSES.has(subInfo.status)) ||
                        (subInfo.cancel_at_period_end && stillActiveUntilEnd) ||
                        (subInfo.status === "canceled" && stillActiveUntilEnd) ||
                        stripeActive;
                }

                const usedCode = (this.extractUsedReferralCode(u) || "").toLowerCase();
                const ownCode = (this.extractOwnReferralCode(u) || "").toLowerCase();

                return {
                    ...u,
                    name, phone, email,
                    referral_code: ownCode || this.extractOwnReferralCode(u) || "",
                    used_referral_code: usedCode,
                    createdAtFormatted: this.extractCreatedAtFormatted(u),

                    currentPeriodEnd,
                    vigencia,

                    status_text: stripeActive ? "Recnet Plus" : "Sin membresía",
                    stripeActive,
                    rowVariant: stripeActive ? "table" : null,
                };
            };

            const enriched = await this.promisePool(merged, worker, 6);
            if (myRun !== this.runId) return;

            // Conteo de referidos, directo desde `app_usuarios` por `referred_by`
            // (como la app). El índice anterior se armaba desde el conjunto
            // fusionado con la colección vieja `usuarios`, cuyo merge por correo
            // borraba el `referred_by` de algunos referidos y dejaba los conteos
            // en cero. La membresía se toma por correo del enriquecido.
            const activePorCorreo = new Map();
            for (const u of enriched) {
                activePorCorreo.set((this.extractEmail(u) || "").toLowerCase(), !!u.stripeActive);
            }

            const conteoRef = {};
            for (const d of this.docsAppUsuarios) {
                const rb = String(d.referred_by || "").trim().toLowerCase();
                if (!rb) continue;
                const email = (this.extractEmail(d) || "").toLowerCase();
                const c = conteoRef[rb] || (conteoRef[rb] = { total: 0, plus: 0 });
                c.total += 1;
                if (activePorCorreo.get(email)) c.plus += 1;
            }

            const withCounts = enriched.map((u) => {
                const ownCode = (u.referral_code || "").toLowerCase();
                const c = conteoRef[ownCode] || { total: 0, plus: 0 };
                return { ...u, refPlusCount: c.plus, refNoCount: c.total - c.plus };
            });

            this.allUsers = withCounts;
            this.plusUsers = withCounts.filter((u) => u.stripeActive);

            this.loadingUsers = false;
            this.firstProcessDone = true;

            const maxPage = Math.max(1, Math.ceil(this.totalRows / this.perPage));
            if (this.currentPage > maxPage) this.currentPage = maxPage;

            this.saveListCache();
        },

        // ===== Referidos (modal) =====
        async computeReferralsFor(user) {
            this.refLoading = true;
            this.refResumen = null;
            try {
                // Se consulta como la app (app_usuarios por `referred_by`) y se
                // resuelve la membresía por referido. El índice interno de esta
                // vista mezcla la colección vieja `usuarios` y perdía referidos.
                const codigo = this.extractOwnReferralCode(user) || user.referral_code || "";
                const lista = await cargarReferidos(codigo);

                const toRow = (r) => ({
                    name: r.name || r.email || "—",
                    email: r.email || "—",
                    phone: r.phone || "—",
                    ownCode: r.referral_code || "—",
                    createdAtFormatted: r.creadoEnFormatted || "—",
                    vigencia: r.activo ? (r.renovacionTexto !== "—" ? `Hasta ${r.renovacionTexto}` : "Vigente") : "—",
                });

                this.refPlus = lista.filter((r) => r.activo).map(toRow);
                this.refNo = lista.filter((r) => !r.activo).map(toRow);
                this.refResumen = resumenReferidos(lista);
            } catch (err) {
                console.error("computeReferralsFor error:", err);
                Swal.fire("Error", "No fue posible cargar los referidos.", "error");
            } finally {
                this.refLoading = false;
            }
        },

        // ===== UI =====
        getRowVariant(item) { return item?.rowVariant || null; },
        async openUserModal(user) {
            this.selectedUser = user;
            await this.computeReferralsFor(user);
            this.showUserModal = true;
        },
        async forceRefresh() {
            try {
                localStorage.removeItem(STRIPE_CACHE_KEY);
                localStorage.removeItem(LIST_CACHE_KEY);
            } catch (err) {
                console.warn("[RecnetPlus] forceRefresh clear cache error:", err);
            }
            this.stripeCache = {};
            this.loadingUsers = true;
            this.firstProcessDone = false;

            await this.loadSubscriptionsIndex();
            this.processUsersCoalesced();
        },
    },

    async mounted() {
        this.loadStripeCache();
        this.rehydrateList();
        await this.loadSubscriptionsIndex();
        this.subscribeUsers();
    },
    beforeDestroy() {
        if (this.debounceTimer) clearTimeout(this.debounceTimer);
        if (this.initialFallbackTimer) clearTimeout(this.initialFallbackTimer);
        this.unsubs.forEach((u) => u && u());
    },
};
</script>

<template>
    <Layout>
        <div class="mb-4">
            <img :src="require('@/assets/images/recnet-header.png')" alt="Recnet Plus"
                class="img-fluid w-100 rounded shadow-sm"
                style="max-height: 260px; object-fit: cover; object-position: center;" />
        </div>

        <div class="row">
            <div class="col-12">
                <div class="card">
                    <div class="card-body">
                        <div class="d-flex justify-content-between align-items-center flex-wrap mb-3">
                            <b-form-group label="Buscar..." label-for="search-input" class="mb-0 flex-grow-1 mr-2">
                                <b-form-input id="search-input" v-model="searchQuery" type="search"
                                    placeholder="Nombre, teléfono o correo…" />
                            </b-form-group>
                            <b-button size="sm" variant="outline-primary" @click="forceRefresh">
                                <i class="mdi mdi-refresh"></i> Forzar refresco
                            </b-button>
                        </div>

                        <b-table responsive hover :items="filteredPlusUsers" :fields="[
                            { key: 'name', label: 'Nombre', sortable: true },
                            { key: 'phone', label: 'Teléfono' },
                            { key: 'email', label: 'Correo' },
                            { key: 'vigencia', label: 'Vigencia (Stripe)' },
                            { key: 'refPlusCount', label: 'Ref. Plus', class: 'text-center' },
                            { key: 'refNoCount', label: 'Ref. sin', class: 'text-center' },
                            { key: 'actions', label: 'Detalles' },
                        ]" :tbody-tr-class="getRowVariant" :per-page="perPage" :current-page="currentPage" show-empty
                            :empty-text="loadingUsers ? 'Cargando…' : 'No hay usuarios activos en Stripe.'">
                            <template #cell(vigencia)="{ item }">
                                <span>{{ item.vigencia || '—' }}</span>
                            </template>

                            <template #cell(refPlusCount)="{ item }">
                                <b-badge variant="success">{{ item.refPlusCount || 0 }}</b-badge>
                            </template>

                            <template #cell(refNoCount)="{ item }">
                                <b-badge variant="danger">{{ item.refNoCount || 0 }}</b-badge>
                            </template>

                            <template #cell(actions)="{ item }">
                                <b-button size="sm" variant="primary" @click="openUserModal(item)">
                                    <i class="mdi mdi-eye"></i> Ver
                                </b-button>
                            </template>
                        </b-table>

                        <b-pagination v-model="currentPage" :total-rows="totalRows" :per-page="perPage" align="right"
                            size="sm" class="mt-3" first-number last-number />
                    </div>
                </div>
            </div>
        </div>

        <b-modal v-model="showUserModal" size="xl" centered hide-footer :title="selectedUser ? selectedUser.name : ''">
            <div v-if="selectedUser" class="evento-card">
                <b-row>
                    <b-col md="12">
                        <div class="evento-titulo mb-2">
                            <h2 class="mb-1">{{ selectedUser.name || 'Sin nombre' }}</h2>
                            <div class="estado-badge activo">
                                <i class="mdi mdi-check-circle"></i>
                                {{ selectedUser.status_text }}
                            </div>
                        </div>

                        <div class="evento-detalles mb-3">
                            <div class="detalle-item">
                                <i class="mdi mdi-email-outline"></i>
                                <span><strong>Correo:</strong> {{ selectedUser.email || '—' }}</span>
                            </div>
                            <div class="detalle-item">
                                <i class="mdi mdi-phone"></i>
                                <span><strong>Teléfono:</strong> {{ selectedUser.phone || '—' }}</span>
                            </div>
                            <div class="detalle-item">
                                <i class="mdi mdi-calendar-refresh"></i>
                                <span><strong>Vigencia (Stripe):</strong> {{ selectedUser.vigencia || '—' }}</span>
                            </div>
                            <div class="detalle-item">
                                <i class="mdi mdi-account-multiple-plus-outline"></i>
                                <span><strong>Código de referido:</strong> {{ selectedUser.referral_code || '—'
                                    }}</span>
                            </div>
                        </div>

                        <div v-if="refLoading" class="text-center text-muted my-3">Cargando referidos…</div>

                        <template v-else>
                            <!-- Comisión del mes: 50 por cada referido activo que
                                 renueva este mes; los que renuevan después cuentan
                                 para el mes que les toca. -->
                            <div v-if="refResumen" class="comision-caja mb-3">
                                <div class="row text-center">
                                    <div class="col">
                                        <div class="comision-num">{{ refResumen.conMembresia }}</div>
                                        <div class="text-muted small">Con Plus</div>
                                    </div>
                                    <div class="col">
                                        <div class="comision-num">{{ refResumen.activosEsteMes }}</div>
                                        <div class="text-muted small">Renuevan este mes</div>
                                    </div>
                                    <div class="col">
                                        <div class="comision-num text-success">{{ formatoMoneda(refResumen.dineroEsteMes) }}</div>
                                        <div class="text-muted small">A pagar este mes</div>
                                    </div>
                                    <div class="col">
                                        <div class="comision-num">{{ formatoMoneda(refResumen.dineroProximoMes) }}</div>
                                        <div class="text-muted small">Próximo mes</div>
                                    </div>
                                </div>
                                <p class="text-muted small mb-0 mt-2 text-center">
                                    {{ formatoMoneda(comision) }} por cada referido con membresía activa, en el mes que renueva.
                                </p>
                            </div>

                            <h5 class="mt-3 mb-2" style="color: var(--primary);">
                                Referidos con Recnet Plus ({{ refPlus.length }})
                            </h5>
                            <b-table small responsive hover :items="refPlus" :fields="[
                                { key: 'name', label: 'Nombre' },
                                { key: 'email', label: 'Correo' },
                                { key: 'phone', label: 'Teléfono' },
                                { key: 'ownCode', label: 'Clave referido' },
                                { key: 'createdAtFormatted', label: 'Fecha de creación' },
                                { key: 'vigencia', label: 'Vigencia (Stripe)' },
                            ]" show-empty empty-text="Sin referidos con membresía." class="mb-4" />

                            <h5 class="mt-3 mb-2" style="color: #dc3545;">
                                Referidos sin membresía ({{ refNo.length }})
                            </h5>
                            <b-table small responsive hover :items="refNo" :fields="[
                                { key: 'name', label: 'Nombre' },
                                { key: 'email', label: 'Correo' },
                                { key: 'phone', label: 'Teléfono' },
                                { key: 'ownCode', label: 'Clave referido' },
                                { key: 'createdAtFormatted', label: 'Fecha de creación' },
                            ]" show-empty empty-text="Sin referidos sin membresía." />
                        </template>
                    </b-col>
                </b-row>
            </div>

            <hr />
            <div class="d-flex justify-content-end">
                <b-button variant="secondary" @click="showUserModal = false">Cerrar</b-button>
            </div>
        </b-modal>
    </Layout>
</template>

<style scoped>
.evento-card {
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
}

.comision-caja {
    background: #f8f9fa;
    border: 1px solid #e9ecef;
    border-radius: 8px;
    padding: 16px;
}

.comision-num {
    font-size: 1.4rem;
    font-weight: 700;
    line-height: 1.1;
}

.evento-titulo h2 {
    font-size: 1.5rem;
    font-weight: 700;
    color: #333;
    margin-bottom: 0;
}

.evento-card hr {
    border: 0;
    border-top: 1px solid #eee;
    margin: 1rem 0;
}

.evento-detalles {
    display: flex;
    flex-direction: column;
    gap: 10px;
}

.detalle-item {
    display: flex;
    align-items: center;
    color: #495057;
    font-size: 0.95rem;
}

.detalle-item .mdi {
    font-size: 20px;
    margin-right: 10px;
    width: 20px;
}

.estado-badge {
    padding: 6px 12px;
    border-radius: 20px;
    font-size: 0.8rem;
    font-weight: 600;
    display: inline-flex;
    align-items: center;
}

.estado-badge.activo {
    background-color: #e6f9f0;
    color: #008a45;
}

.estado-badge .mdi {
    margin-right: 4px;
}
</style>
